<template>

  <v-container fluid class="pa-0 order-provider-compare-utility">

    <!-- (data loading spinner) -->
    <v-container class="ma-0 pl-0 py-3 d-flex align-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="22" color="primary" />
      <div class="pl-4 loading-text">{{ loadingText }}</div>
    </v-container>

    <v-container class="pa-0 ma-0" v-else>

      <!--------------------------->
      <!-- Compare Action Button -->
      <!--------------------------->
      <v-row v-if="!hideCompareAction">
        <v-col class="d-flex align-center">
          <!-- <div class="provider-logo-container"><leaf-link-logo /></div>
          <div class="m-dash-text">&mdash;</div> -->
          <v-btn
              class="pl-3"
              color="primary"
              :disabled="isOrderUpdating"
              @click="performCompare">
            <v-icon class="pr-2">mdi-compare-horizontal</v-icon>
            <span>Compare</span>
          </v-btn>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="grey lighten-1"
                  style="font-size:20px;cursor:pointer;margin-left:12px"
                  v-bind="attrs"
                  v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <div>{{ performCompareInfoText }}</div>
          </v-tooltip>
        </v-col>
      </v-row>

      <!-- Data Loading Error -->
      <v-container class="ma-0" v-if="dataLoadingError && dataLoadingError !== 'Not Found'">
        <transition name="fade" appear>
          <v-row class="data-loading-error">
            <v-icon class="pr-2" color="red">mdi-alert-circle-outline</v-icon>
            <div>{{ dataLoadingError }}</div>
          </v-row>
        </transition>
      </v-container>

      <!------------------------------------>
      <!-- Result: Provider Order Missing -->
      <!------------------------------------>
      <template v-else-if="dataLoadingError && dataLoadingError === 'Not Found'">
        <v-row>
          <v-col cols="12" xs="12" sm="12" md="4" lg="3" class="d-flex align-center">
            <v-progress-circular indeterminate :size="30" color="primary" v-if="isOrderUpdating" />
            <template v-else>
              <v-icon color="amber darken-2" class="mr-2 alert-icon">mdi-alert-outline</v-icon>
              <div style="font-weight:500;color:#FF9F00;" class="mr-4">{{ $root.providerAliasToName(this.providerAlias) }} order is gone</div>
            </template>
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="3" lg="3">
            <!-- Cancel order action -->
            <dialog-cancel-order-button
                :disabled="isOrderUpdating"
                :order="order"
                @confirmed="cancelOrder" />
          </v-col>
        </v-row>
      </template>

      <template v-else-if="compareResult">
        <!---------------------------------->
        <!-- Provider Status with Actions -->
        <!---------------------------------->
        <v-row :class="hideCompareAction ? 'mt-0' : 'mt-6'">
          <v-col cols="12" xs="12" sm="5" md="5" lg="3" class="d-flex align-center provider-status-compare-result">
            <div class="pr-4 attr-label">{{ providerStatusLabelText }}:</div>
            <div>{{ compareResult.thirdparty_status }}</div>
          </v-col>
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <!-- Cancel order action -->
            <dialog-cancel-order-button
                v-if="isProviderOrderCanceled"
                :disabled="isOrderUpdating"
                :order="order"
                @confirmed="cancelOrder" />
            <!-- Sync data action -->
            <div class="d-flex align-center" v-else-if="!diff.same && !isProviderOrderCanceled">
              <v-progress-circular indeterminate :size="30" style="padding-left:12px;" color="primary" v-if="isOrderUpdating" />
              <template v-else>
                <dialog-confirm-button
                    buttonColor="orange darken-3"
                    buttonText="Sync Changes"
                    confirmButtonColor="orange darken-3"
                    confirmButtonText="Sync Order Data"
                    message="Please ensure to re-upload your Supporting and Attachment docs after syncing."
                    v-on:confirmed="syncProviderData" />
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="grey lighten-1"
                        style="font-size:20px;cursor:pointer;margin-left:11px"
                        v-bind="attrs"
                        v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div>Sync order changes from {{ $root.providerAliasToName(this.providerAlias) }}</div>
                </v-tooltip>
              </template>
            </div>
          </v-col>
        </v-row>

        <!------------------------>
        <!-- Result: No Changes -->
        <!------------------------>
        <v-row class="mt-3" v-if="diff.same">
          <v-col cols="12" xs="12" sm="12" md="12" lg="12" class="d-flex align-center">
            <v-icon color="green" class="pr-2 no-change-icon">mdi-check-circle</v-icon>
            <div>No order changes</div>
          </v-col>
        </v-row>

        <!------------------------->
        <!-- Result: Has Changes -->
        <!------------------------->
        <v-row class="mt-3" v-else-if="!isProviderOrderCanceled">
          <v-col cols="12" xs="12" sm="12" md="12" lg="12">
            <div class="d-flex align-center">
              <v-icon color="amber darken-2" class="mr-2 alert-icon">mdi-alert-outline</v-icon>
              <div style="font-size:1.08rem;font-weight:500;color:#FF9F00;" class="mr-4">
                {{ $root.providerAliasToName(this.providerAlias) }} data has changed
              </div>
              <v-btn text color="#727272" @click="showChangeOutline = !showChangeOutline">
                <span class="pr-2" v-if="showChangeOutline">Hide changes</span>
                <span class="pr-2" v-else>Show changes</span>
                <v-icon v-if="!showChangeOutline">mdi-chevron-down</v-icon>
                <v-icon v-else>mdi-chevron-up</v-icon>
              </v-btn>
            </div>
            <transition name="slide-down" appear>
              <div style="max-width:840px;" class="pl-5 pt-6" v-if="showChangeOutline">
                <div class="mb-2" v-if="orderTotalChangeText">
                  <v-icon color="red accent-4" class="pr-2 change-summary-icon">mdi-not-equal-variant</v-icon>
                  <span>{{ orderTotalChangeText }}</span>
                </div>
                <div class="mb-2" v-if="orderedItemsAddedText">
                  <v-icon color="red accent-4" class="pr-2 change-summary-icon">mdi-not-equal-variant</v-icon>
                  <span>{{ orderedItemsAddedText }}</span>
                </div>
                <div class="mb-2" v-if="orderedItemsRemovedText">
                  <v-icon color="red accent-4" class="pr-2 change-summary-icon">mdi-not-equal-variant</v-icon>
                  <span>{{ orderedItemsRemovedText }}</span>
                </div>
                <div class="mb-2" v-if="itemDetailsChangedText">
                  <v-icon color="red accent-4" class="pr-2 change-summary-icon">mdi-not-equal-variant</v-icon>
                  <span>{{ itemDetailsChangedText }}</span>
                </div>
                <div class="mb-2" v-if="batchAssociationsChangedText">
                  <v-icon color="red accent-4" class="pr-2 change-summary-icon">mdi-not-equal-variant</v-icon>
                  <span>{{ batchAssociationsChangedText }}</span>
                </div>
                <div class="mt-6">
                  <v-btn text color="primary" @click="isDetailedOrderChangesDialogOpen = true">
                    <v-icon small class="mr-2">mdi-magnify</v-icon>
                    <span>View Details</span>
                  </v-btn>
                </div>
              </div>
            </transition>
          </v-col>
        </v-row>
      </template>

    </v-container>

    <!----------------------------------->
    <!-- Detailed Order Changes Dialog -->
    <!----------------------------------->
    <v-dialog persistent max-width="850" :fullscreen="isMobileView" v-model="isDetailedOrderChangesDialogOpen">
      <v-card class="detailed-order-changes-preview">
        <v-card-text>
          <order-provider-compare-results
              :compareResult="compareResult"
              :showProviderStatus="true"
              v-on:close="isDetailedOrderChangesDialogOpen = false" />
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
// import LeafLinkLogo from '@/components/app/LeafLinkLogo'
import OrderProviderCompareResults from '@/components/orders/OrderProviderCompareResults'
import DialogCancelOrderButton from '@/components/orders/DialogCancelOrderButton'
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'OrderProviderCompareUtility',

  components: {
    // LeafLinkLogo,
    OrderProviderCompareResults,
    DialogCancelOrderButton,
    DialogConfirmButton
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    compareOnMount: {
      type: Boolean,
      default: false
    },
    hideCompareAction: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      isOrderUpdating: false,
      orderUpdatingError: null,
      showChangeOutline: false,
      isDetailedOrderChangesDialogOpen: false,
      compareResult: null,
      compareResultForDeving: {
        model: 'ProviderOrderDiff',
        id: '',
        provider_alias: 'leaflink',
        order_id: '3d464a9d-edef-4335-9609-6c13816c499c',
        order_number: 'INV#295961',
        thirdparty_order_id: '4c3b5e4d-8497-4701-aedc-51554d4324c5',
        // thirdparty_status: 'cancelled',
        thirdparty_status: 'fulfilled',
        compared_at: '2024-06-16T10:20:37.871Z',
        diffX: { same: true },
        diff: {
          order_total: {
            routeleaf: 2520,
            provider: 2620
          },
          ordered_items: {
            routeleaf: [
              {
                product_id: 46930420,
                batch_number: '9010S.007.0324.06.100.KPKB',
                product_label: '100mg Sativa Gummies Kiwi Breeze - j [H:11/20/23; M:3/1/24; EXP:3/1/26] 6350 7300 2556 7953'
              }
            ],
            provider: [
              {
                product_id: 46930417,
                batch_number: '9010I.007.0324.06.100.KPB',
                product_label: '100mg Indica Gummies Blueberry - f [H:11/20/23; M:3/1/24; EXP:3/1/26] 1266 7313 7252 7746'
              },
              {
                product_id: 46930418,
                batch_number: '9010S.006.1123.06.100.KPL',
                product_label: '100mg Sativa Gummies Lemonade - f [H:7/25/23; M:11/1/23; EXP:11/1/25] 6935 0670 3328 2469'
              }
            ]
          },
          item_changes: [
            {
              product_id: 46930411,
              product_label: '100mg Indica Gummies Peach Mango - a [H:11/20/23; M:3/1/24; EXP:3/1/26] 4800 2059 0085 2378',
              changes: {
                quantity: {
                  routeleaf: '2 Cases (60 Units)',
                  provider: '1 Case (30 Units)'
                },
                item_total: {
                  routeleaf: 360,
                  provider: 180
                }
              }
            }
          ],
          batch_associations: [
            {
              product_id: 46930411,
              batch_number: '9010I.007.0324.06.100.KPPM',
              product_label: '100mg Indica Gummies Peach Mango - a [H:11/20/23; M:3/1/24; EXP:3/1/26] 4800 2059 0085 2378',
              provider_batch_number: '9010I.007.0324.06.105.KPPM'
            },
            {
              product_id: 46930416,
              batch_number: '9010H.007.0324.06.100.KPBR',
              product_label: '100mg Hybrid Gummies Blue Raspberry - e [H:11/20/23; M:3/1/24; EXP:3/1/26] 5996 0997 0142 4845',
              provider_batch_number: '9010Z.007.0324.06.100.KPBR'
            }
          ]
        }
      }
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    isMobileView () {
      return (window && window.innerWidth && window.innerWidth < 600)
    },
    providerAlias () {
      return this.order.provider_alias
    },
    performCompareInfoText () {
      return 'Check for changes on the provider order.'
    },
    loadingText () {
      return (this.providerAlias)
        ? `Comparing order data with ${this.$root.providerAliasToName(this.providerAlias)}`
        : 'Comparing order data with provider'
    },
    providerStatusLabelText () {
      return (this.providerAlias)
        ? `${this.$root.providerAliasToName(this.providerAlias)} status`
        : 'Provider status'
    },
    diff () {
      return this.compareResult?.diff
    },
    isProviderOrderCanceled () {
      return (this.compareResult)
        ? this.compareResult.thirdparty_status === 'rejected' || this.compareResult.thirdparty_status === 'cancelled'
        : false
    },
    // --------------------------------- Possible changes
    orderTotalChangeText () {
      return (this.diff?.order_total) ? 'Order total has changed' : null
    },
    orderedItemsAddedText () {
      return (this.diff?.ordered_items?.provider)
        ? (this.diff.ordered_items.provider.length === 1)
          ? '1 ordered item added'
          : `${this.diff.ordered_items.provider.length} ordered items added`
        : null
    },
    orderedItemsRemovedText () {
      return (this.diff?.ordered_items?.routeleaf)
        ? (this.diff.ordered_items.routeleaf.length === 1)
          ? '1 ordered item removed'
          : `${this.diff.ordered_items.routeleaf.length} ordered items removed`
        : null
    },
    itemDetailsChangedText () {
      return (this.diff?.item_changes)
        ? (this.diff.item_changes.length === 1)
          ? '1 ordered item has changed details'
          : `${this.diff.item_changes.length} ordered items have changed details`
        : null
    },
    batchAssociationsChangedText () {
      return (this.diff?.batch_associations)
        ? (this.diff.batch_associations.length === 1)
          ? '1 batch has changed'
          : `${this.diff.batch_associations.length} batches have changed`
        : null
    }
  },

  async mounted () {
    // Perform compare on mount, if specified
    if (this.compareOnMount) this.performCompare()
  },

  methods: {
    // Clear active errors
    clearError () {
      this.dataLoadingError = null
    },

    // Perform comparison to active provider order
    async performCompare () {
      this.isDataLoading = true

      try {
        this.compareResult = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/provider-compare`
        })

        // ---------------- DEVING
        // const trueResult = await this.$store.dispatch('sh/dataApi', {
        //   method: 'get',
        //   uri: `order/${this.order.id}/provider-compare`
        // })
        // console.log('[DEVING] RESULT ==>', trueResult)
        // this.compareResult = this.compareResultForDeving
        // ---------------- DEVING

        // DEVING
        // this.dataLoadingError = 'There was a problem loading data from the provider.'
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    // Handle provider data sync
    async syncProviderData () {
      this.isOrderUpdating = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${this.order.id}/provider-sync`
        })

        // Emit updated event
        this.$emit('updated', 'synced')
      } catch (err) {
        if (this.$root.utils.isEmpty(err)) this.orderUpdatingError = 'The provided data could not be synced.'
        else this.orderUpdatingError = err.message || err.text || err
      } finally {
        this.isOrderUpdating = false
      }
    },

    // Handle order cancel
    // data.order = the order to cancel
    // data.notes = the dispatcher notes (optional)
    async cancelOrder (data = {}) {
      const orderToCancel = data.order
      // const notes = data.notes
      if (!orderToCancel) return false

      this.isOrderUpdating = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${this.order.id}/cancel`
        })

        // Emit updated event
        this.$emit('updated', 'canceled')
      } catch (err) {
        if (this.$root.utils.isEmpty(err)) this.orderUpdatingError = 'The order could not be canceled.'
        else this.orderUpdatingError = err.message || err.text || err
      } finally {
        this.isOrderUpdating = false
      }
    }
  }
}

</script>

<style scoped>
  .order-provider-compare-utility .loading-text {
    font-size: 1.0rem;
    font-weight: 400;
    color: #90A4AE;
  }

  .order-provider-compare-utility .m-dash-text {
    color: #727272;
    padding-top: 3px;
    padding-right: 15px;
    padding-left: 14px;
  }

  .order-provider-compare-utility .provider-logo-container {
    padding-left: 5px;
    padding-right: 5px;
    padding-top: 5px;
    border-radius: 2px;
    background-color: #f2f2f2;
  }

  .order-provider-compare-utility .data-loading-error {
    color: #F44336;
  }

  .order-provider-compare-utility .no-change-icon {
    font-size: 28px;
  }

  .order-provider-compare-utility .change-summary-icon {
    font-size: 22px;
  }

  .order-provider-compare-utility .alert-icon {
    font-size: 24px;
  }

  .order-provider-compare-utility .detailed-order-changes-preview {
    background-color: #121212 !important;
  }

  .order-provider-compare-utility .provider-status-compare-result {
    font-size: 1.08rem;
  }

  /* .v-dialog:not(.v-dialog--fullscreen) {
    max-height: 80%;
  } */

  @media screen and (max-width: 600px) {
    .detailed-order-changes-preview .v-card__text {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
</style>
